import React, { useState } from "react";
import axios from "axios";
import { ShoppingBag, Plus, Minus, Trash2, ArrowRight, Truck, Send, Copy, Check } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import WhatsAppIcon from "../WhatsAppIcon";
import { buildCartWhatsAppUrl, buildBillText, buildBillShareUrl, gstBreakup, SHOP_NAME } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CartStage({
  items, updateQty, removeItem, setLineDiscount, subtotal, baseSubtotal, gst, includeGst, setIncludeGst,
  includeDelivery, setIncludeDelivery, deliveryFeeValue, setDeliveryFee,
  productsTotal, deliveryFee, total, savings, onCheckout,
}) {
  const { user } = useAuth();
  const canEditDiscount = !!user?.can_edit_discount;
  const [billPhone, setBillPhone] = useState("");
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [billErr, setBillErr] = useState("");
  const [pdfLink, setPdfLink] = useState("");

  const phoneValid = billPhone.replace(/\D/g, "").replace(/^0+/, "").length >= 10;

  const generateInvoice = async () => {
    const res = await axios.post(`${BACKEND}/api/invoices`, {
      include_gst: includeGst,
      delivery_fee: deliveryFee,
      items: items.map((i) => ({
        product_id: i.product_id,
        name: i.name,
        price: i.price,
        mrp: i.mrp,
        qty: i.qty,
        discount_pct: i.discount_edited ? i.discount_pct || 0 : 0,
        base_price: i.mrp || i.base_price || i.price,
        variant_id: i.variantInfo?.variant_id,
        selections: i.variantInfo?.selections,
      })),
    });
    return { pdfUrl: `${BACKEND}${res.data.url}`, ...res.data };
  };

  const sendBill = async () => {
    if (!phoneValid || sending) return;
    setBillErr("");
    setPdfLink("");
    setSending(true);
    let inv;
    try {
      inv = await generateInvoice();
    } catch {
      setBillErr("Could not generate estimate. Please try again.");
      setSending(false);
      return;
    }
    setSending(false);
    setPdfLink(inv.pdfUrl);
    const msg = `Hi! Estimate from ${SHOP_NAME}.\n\n*Estimate ${inv.invoice_no}*\nGrand Total: ₹${inv.grand_total.toLocaleString("en-IN")}${includeGst ? " (incl. 18% GST)" : " (without GST)"}\n\n📄 Download PDF estimate:\n${inv.pdfUrl}`;
    const url = buildBillShareUrl(billPhone, msg);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) window.location.href = url;
  };

  const copyPdfLink = async () => {
    if (sending) return;
    setBillErr("");
    setPdfLink("");
    setSending(true);
    let inv;
    try {
      inv = await generateInvoice();
    } catch {
      setBillErr("Could not generate estimate. Please try again.");
      setSending(false);
      return;
    }
    setSending(false);
    setPdfLink(inv.pdfUrl);
    try {
      await navigator.clipboard.writeText(inv.pdfUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard unavailable (mobile webview / permissions) — link shown below instead
    }
  };

  const shareBill = copyPdfLink;

  if (items.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div data-testid={SHOP.cartEmpty} className="h-full flex flex-col items-center justify-center text-center py-16">
          <div className="w-20 h-20 rounded-full bg-gray-100 grid place-items-center mb-4">
            <ShoppingBag size={32} className="text-gray-400" />
          </div>
          <p className="text-gray-900 font-bold">Your cart is empty</p>
          <p className="text-sm text-gray-500 mt-1">Add some electrical goodies to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-[65_1_0%] min-h-[90px] overflow-y-auto px-5 py-4">
        <ul className="space-y-4">
          {items.map((i) => (
            <li key={i.product_id} data-testid={SHOP.cartItem(i.product_id)} className="flex gap-3 pb-4 border-b border-gray-100 last:border-0">
              <img src={i.image} alt={i.name} className="w-16 h-16 rounded-lg object-cover bg-gray-50 border border-gray-200 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[#0056D2] font-bold">{i.brand}</div>
                <div className="text-sm font-semibold text-gray-900 line-clamp-2">{i.name}</div>
                {i.variantInfo?.selections && (
                  <div className="text-[11px] text-gray-600 mt-0.5">
                    {Object.entries(i.variantInfo.selections)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(" · ")}
                  </div>
                )}
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-sm font-extrabold text-gray-900">₹{i.price.toLocaleString("en-IN")}</span>
                  {i.mrp > i.price && (
                    <>
                      <span className="text-[11px] text-gray-500 line-through">MRP ₹{i.mrp.toLocaleString("en-IN")}</span>
                      <span className="text-[10px] font-bold text-[#16A34A]">{Math.round(((i.mrp - i.price) / i.mrp) * 1000) / 10}% off</span>
                    </>
                  )}
                </div>
                <div className="text-[10px] text-gray-500">
                  {i.hsn_code && <span className="font-semibold">HSN {i.hsn_code} · </span>}
                  ₹{gstBreakup(i.price * i.qty).base.toLocaleString("en-IN")} + ₹{gstBreakup(i.price * i.qty).gst.toLocaleString("en-IN")} GST
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center bg-gray-100 rounded-md">
                      <button onClick={() => updateQty(i.product_id, i.qty - 1)} className="w-8 h-8 grid place-items-center hover:bg-gray-200 rounded-l-md" aria-label="Decrease">
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center text-sm font-bold">{i.qty}</span>
                      <button onClick={() => updateQty(i.product_id, i.qty + 1)} className="w-8 h-8 grid place-items-center hover:bg-gray-200 rounded-r-md" aria-label="Increase">
                        <Plus size={14} />
                      </button>
                    </div>
                    {canEditDiscount && (
                      <div className="inline-flex items-center gap-1 rounded-md border border-dashed border-[#FF5722]/50 bg-[#FFF3EF] px-1.5 h-8">
                        <span className="text-[9px] font-black uppercase text-[#E64A19]">Disc</span>
                        <input
                          data-testid={`cart-discount-${i.product_id}`}
                          inputMode="numeric"
                          value={i.discount_pct || ""}
                          placeholder="0"
                          onChange={(e) => setLineDiscount(i.product_id, e.target.value.replace(/\D/g, "").slice(0, 2))}
                          className="w-8 bg-transparent text-center text-xs font-extrabold text-[#E64A19] outline-none"
                        />
                        <span className="text-[10px] font-black text-[#E64A19]">%</span>
                      </div>
                    )}
                  </div>
                  <button
                    data-testid={SHOP.cartRemove(i.product_id)}
                    onClick={() => removeItem(i.product_id)}
                    className="text-xs text-gray-500 hover:text-[#FF5722] inline-flex items-center gap-1 font-semibold"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-gray-200 p-5 bg-gray-50 flex-[35_1_0%] min-h-0 overflow-y-auto">
        <div className="space-y-1.5 mb-3">
          <div className="flex justify-between text-sm text-gray-700"><span>Sub Total (excl. GST)</span><span data-testid={SHOP.cartSubtotal} className="font-bold">₹{baseSubtotal.toLocaleString("en-IN")}</span></div>
          <div className="flex justify-between text-sm text-gray-700">
            <span>GST (18%)</span>
            <span data-testid="cart-gst-amount" className={`font-bold ${includeGst ? "" : "line-through text-gray-400"}`}>₹{gst.toLocaleString("en-IN")}</span>
          </div>
          <label className="flex items-center justify-between text-xs text-gray-600 cursor-pointer select-none">
            <span>Include GST in final bill</span>
            <input
              data-testid="cart-gst-toggle"
              type="checkbox"
              checked={includeGst}
              onChange={(e) => setIncludeGst(e.target.checked)}
              className="w-4 h-4 accent-[#0056D2] cursor-pointer"
            />
          </label>
          <div className="flex justify-between text-sm text-gray-700 pt-1 border-t border-gray-200">
            <span className="font-bold">Products total (without delivery)</span>
            <span data-testid="cart-products-total" className="font-bold">₹{productsTotal.toLocaleString("en-IN")}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-700 gap-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                data-testid="cart-delivery-toggle"
                type="checkbox"
                checked={includeDelivery}
                onChange={(e) => setIncludeDelivery(e.target.checked)}
                className="w-4 h-4 accent-[#0056D2] cursor-pointer"
              />
              <span>Delivery charges</span>
            </label>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">₹</span>
              <input
                data-testid="cart-delivery-input"
                type="number"
                min="0"
                value={deliveryFeeValue}
                disabled={!includeDelivery}
                onChange={(e) => setDeliveryFee(Math.max(0, Number(e.target.value) || 0))}
                className={`w-16 h-7 px-2 rounded-md border text-right text-sm font-bold ${includeDelivery ? "border-gray-300 focus:border-[#0056D2]" : "border-gray-200 bg-gray-100 text-gray-400"} outline-none`}
              />
            </div>
          </div>
          {savings > 0 && (
            <div className="flex justify-between text-xs text-[#16A34A] font-semibold"><span>You save</span><span>₹{savings.toLocaleString("en-IN")}</span></div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-200 mt-2"><span className="font-extrabold text-gray-900">Grand Total{includeDelivery ? " (with delivery)" : " (without delivery)"}{includeGst ? "" : " · GST removed"}</span><span data-testid="cart-total" className="font-extrabold text-lg text-gray-900">₹{total.toLocaleString("en-IN")}</span></div>
        </div>
        <button
          data-testid={SHOP.cartCheckout}
          onClick={onCheckout}
          className="w-full h-12 rounded-lg bg-[#FF5722] hover:bg-[#E64A19] text-white font-extrabold uppercase tracking-wide text-sm inline-flex items-center justify-center gap-2 transition-colors"
        >
          Checkout <ArrowRight size={16} />
        </button>
        <a
          data-testid={SHOP.cartWhatsappBtn}
          href={buildCartWhatsAppUrl(items, total)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 w-full h-11 rounded-lg bg-[#25D366] hover:bg-[#1FB855] text-white font-extrabold uppercase tracking-wide text-xs inline-flex items-center justify-center gap-2 transition-colors"
        >
          <WhatsAppIcon size={16} /> Order on WhatsApp
        </a>

        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-2">Share Quote (PDF)</div>
          <div className="flex gap-2">
            <input
              data-testid="bill-phone-input"
              value={billPhone}
              onChange={(e) => setBillPhone(e.target.value)}
              placeholder="Mobile number e.g. 9876543210"
              className="flex-1 h-9 px-3 rounded-md border border-gray-300 focus:border-[#0056D2] outline-none text-xs"
            />
            <button
              data-testid="bill-send-btn"
              onClick={sendBill}
              disabled={!phoneValid || sending}
              className={`h-9 px-3 rounded-md inline-flex items-center gap-1.5 text-xs font-bold text-white ${phoneValid && !sending ? "bg-[#25D366] hover:bg-[#1FB855]" : "bg-gray-300 cursor-not-allowed"}`}
            >
              <Send size={13} /> {sending ? "PDF..." : "Send PDF"}
            </button>
            <button
              data-testid="bill-share-btn"
              onClick={shareBill}
              className="h-9 px-3 rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5 text-xs font-bold text-gray-700"
            >
              {copied ? <><Check size={13} className="text-[#16A34A]" /> Copied</> : <><Copy size={13} /> Copy</>}
            </button>
          </div>
          {billErr && <div className="mt-1.5 text-[11px] text-[#EF4444] font-semibold">{billErr}</div>}
          {pdfLink && (
            <div data-testid="bill-pdf-link" className="mt-1.5 text-[11px] font-semibold break-all">
              📄 <a href={pdfLink} target="_blank" rel="noopener noreferrer" className="text-[#0056D2] underline">Open PDF estimate</a>
            </div>
          )}
          <div className="mt-1.5 text-[10px] text-gray-400">Sends a PDF quote on WhatsApp — items, qty, discount, HSN &amp; totals, {includeGst ? "with" : "without"} GST (per the toggle above). Copy gives the PDF link.</div>
        </div>

        <div className="mt-2 text-[11px] text-center text-gray-500 inline-flex items-center justify-center gap-1 w-full">
          <Truck size={12} /> Free delivery on orders above ₹500 · fastest delivery
        </div>
      </div>
    </>
  );
}
